import React from "react";
import Select from "react-select";

/**
 * CommonSelect - Premium rounded pill react-select dropdown.
 *
 * Props:
 *   label      {string}   Label shown above the select
 *   required   {boolean}  Appends a red asterisk to the label
 *   options    {array}    Array of { value, label } options
 *   value      {object}   Currently selected option object
 *   onChange   {function} Called with the selected option object
 *   placeholder{string}
 *   isClearable{boolean}
 *   isDisabled {boolean}
 *   height     {number|string} Height of the select (default: 45)
 *   error      {string}   Error message to display below
 *   labelClass {string}   Extra classes for the label
 *   wrapClass  {string}   Extra classes for the wrapping div
 */
const CommonSelect = ({
  label,
  required = false,
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  isClearable = false,
  isDisabled = false,
  disabled,
  height = 45,
  error,
  labelClass = "",
  wrapClass = "",
  formatOptionLabel,
  components,
  styles = {},
}) => {
  const isActuallyDisabled = isDisabled || disabled;

  const customStyles = {
    control: (base, state) => ({
      ...base,
      height: height,
      minHeight: height,
      borderRadius: "0.75rem",
      padding: "0 8px",
      border: error
        ? "1px solid #ff5e5e"
        : state.isFocused
          ? "1px solid #003b95"
          : "1px solid #d5dfe7",
      boxShadow: "none",
      backgroundColor: isActuallyDisabled ? "#f8f9fa" : "#fff",
      "&:hover": {
        border: error ? "1px solid #1eaae7" : "1px solid #003b95",
      },
      ...styles.control?.(base, state),
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 12px",
      ...styles.valueContainer?.(base),
    }),
    placeholder: (base) => ({
      ...base,
      color: "#adb5bd",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...styles.placeholder?.(base),
    }),
    singleValue: (base) => ({
      ...base,
      color: isActuallyDisabled ? "gray" : "#333",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...styles.singleValue?.(base),
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: height,
      paddingRight: "8px",
      ...styles.indicatorsContainer?.(base),
    }),
    indicatorSeparator: () => ({ display: "none" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#1eaae7"
        : state.isFocused
          ? "#e8effc"
          : "#fff",

      color: state.isSelected ? "#fff" : "#333",
      "&:active": {
        backgroundColor: "#1eaae7",
        color: "#fff",
      },
      ...styles.option?.(base, state),
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "10px",
      overflow: "hidden",
      zIndex: 9999,
      width: "max-content",
      minWidth: "100%",
      ...styles.menu?.(base),
    }),
  };

  return (
    <div className={wrapClass}>
      {label && (
        <label className={`fw-bold text-black fs-14 mb-2 ${labelClass}`}>
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      <Select
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isClearable={isClearable}
        isDisabled={isActuallyDisabled}
        styles={customStyles}
        formatOptionLabel={formatOptionLabel}
        components={components}
      />
      {error && <div className="text-danger fs-12 mt-1 ms-3">{error}</div>}
    </div>
  );
};

export default CommonSelect;
